import React, { useEffect, useState } from 'react';
import { PlusOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, DatePicker, Form, Input, message, Modal, Select, Space, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { useSnapshot } from 'valtio';
import {
  changeGoodsRecommendTaskStatusApi,
  deleteGoodsRecommendTaskApi,
  getGoodsRecommendTaskListApi,
} from '@/api/pointCenter/goodsRecommend';
import { GOODS_RECOMMEND_KEY } from '@/router/Permissions';
import { state } from '@/store';
import { checkPermissions } from '@/utils/utils';
import TaskDrawer from './components/TaskDrawer';
import { formatRecommendLogic, isTaskActive, TASK_STATUS_LABEL_MAP, TASK_STATUS_OPTIONS } from './constants';

const { RangePicker } = DatePicker;

type TaskDrawerMode = 'create' | 'edit' | 'view';

const TASK_STATUS_STYLES: Record<number, React.CSSProperties> = {
  0: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 76,
    padding: '2px 12px',
    borderRadius: 6,
    border: '1px solid #d9d9d9',
    background: '#fafafa',
    color: '#595959',
    lineHeight: '22px',
  },
  1: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 76,
    padding: '2px 12px',
    borderRadius: 6,
    border: '1px solid #95de64',
    background: '#f6ffed',
    color: '#52c41a',
    lineHeight: '22px',
  },
  2: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 76,
    padding: '2px 12px',
    borderRadius: 6,
    border: '1px solid #ffd591',
    background: '#fff7e6',
    color: '#d48806',
    lineHeight: '22px',
  },
};

const GoodsRecommendList: React.FC = () => {
  const [form] = Form.useForm();
  const snap = useSnapshot(state);
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerMode, setDrawerMode] = useState<TaskDrawerMode>('create');
  const [currentTaskId, setCurrentTaskId] = useState<number | string | null>(null);

  const activeShopId = snap.currentShopId;

  useEffect(() => {
    if (!activeShopId) {
      setDataSource([]);
      setTotal(0);
      return;
    }
    fetchTaskList({ pageNum: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeShopId]);

  const fetchTaskList = async (override?: Partial<{ pageNum: number; pageSize: number }>) => {
    if (!activeShopId) {
      message.warning('请先选择店铺');
      return;
    }
    const values = form.getFieldsValue();
    const taskStartTime: Dayjs[] | undefined = values.startTime;
    const updateTime: Dayjs[] | undefined = values.updateTime;
    const params = {
      shopId: activeShopId,
      taskName: values.taskName || undefined,
      status:
        values.status === undefined || values.status === null || values.status === ''
          ? undefined
          : Number(values.status),
      startTimeBegin: taskStartTime?.[0] ? taskStartTime[0].startOf('day').format('YYYY-MM-DD HH:mm:ss') : undefined,
      startTimeEnd: taskStartTime?.[1] ? taskStartTime[1].endOf('day').format('YYYY-MM-DD HH:mm:ss') : undefined,
      updateTimeBegin: updateTime?.[0] ? updateTime[0].startOf('day').format('YYYY-MM-DD HH:mm:ss') : undefined,
      updateTimeEnd: updateTime?.[1] ? updateTime[1].endOf('day').format('YYYY-MM-DD HH:mm:ss') : undefined,
      pageNum: override?.pageNum || pageNum,
      pageSize: override?.pageSize || pageSize,
    };
    setLoading(true);
    try {
      const res = await getGoodsRecommendTaskListApi(params);
      if (res?.code === 200) {
        setDataSource(res.rows || []);
        setTotal(res.total || 0);
        if (override?.pageNum) setPageNum(override.pageNum);
        if (override?.pageSize) setPageSize(override.pageSize);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const openDrawer = (mode: TaskDrawerMode, record?: any) => {
    if (!activeShopId) {
      message.warning('请先选择店铺');
      return;
    }
    setDrawerMode(mode);
    setCurrentTaskId(record?.id ?? null);
    setDrawerVisible(true);
  };

  const handleDeleteTask = (record: any) => {
    if (!activeShopId) {
      message.warning('请先选择店铺');
      return;
    }
    Modal.confirm({
      title: '确认删除该任务？',
      okText: '删除',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const res = await deleteGoodsRecommendTaskApi({ shopId: activeShopId, id: record.id });
          if (res?.code === 200) {
            message.success('删除成功');
            const nextPageNum = dataSource.length === 1 && pageNum > 1 ? pageNum - 1 : pageNum;
            fetchTaskList({ pageNum: nextPageNum });
          }
        } catch (error) {
          console.error(error);
        }
      },
    });
  };

  const handleActivateTask = (record: any) => {
    if (!activeShopId) {
      message.warning('请先选择店铺');
      return;
    }
    Modal.confirm({
      title: '确认将该任务设为生效中？',
      onOk: async () => {
        try {
          const res = await changeGoodsRecommendTaskStatusApi({
            shopId: activeShopId,
            id: record.id,
            status: 1,
          });
          if (res?.code === 200) {
            message.success('任务生效成功');
            fetchTaskList({ pageNum: 1 });
          }
        } catch (error) {
          console.error(error);
        }
      },
    });
  };

  const renderTaskTime = (record: any, type: 'create' | 'effective' | 'update') => {
    if (type === 'create') {
      return record.createTime || record.createDate || record.gmtCreate || record.startTime || '-';
    }
    if (type === 'effective') {
      return record.effectiveTime || record.startTime || '-';
    }
    return record.updateTime || record.gmtModified || '-';
  };

  const columns: ColumnsType<any> = [
    {
      title: '任务名称',
      dataIndex: 'taskName',
      width: 220,
    },
    {
      title: '任务创建时间',
      width: 180,
      render: (_, record) => renderTaskTime(record, 'create'),
    },
    {
      title: '任务生效时间',
      width: 180,
      render: (_, record) => renderTaskTime(record, 'effective'),
    },
    {
      title: '最近一次更新时间',
      dataIndex: 'updateTime',
      width: 180,
      render: (_, record) => renderTaskTime(record, 'update'),
    },
    {
      title: '商品推荐逻辑',
      dataIndex: 'recommendOrderList',
      width: 220,
      render: value => formatRecommendLogic(value, snap.currentShopType),
    },
    {
      title: '任务状态',
      dataIndex: 'status',
      width: 120,
      render: value => {
        const statusValue = Number(value);
        const label = TASK_STATUS_LABEL_MAP[String(statusValue)] || '-';
        const style = TASK_STATUS_STYLES[statusValue];
        if (!style) {
          return label;
        }
        return <span style={style}>{label}</span>;
      },
    },
    {
      title: '操作',
      width: 260,
      fixed: 'right',
      render: (_, record) => (
        <Space size={4}>
          <Button
            type="link"
            onClick={() => openDrawer('view', record)}>
            查看
          </Button>
          {checkPermissions(GOODS_RECOMMEND_KEY.UPDATE) && (
            <Button
              type="link"
              onClick={() => openDrawer('edit', record)}>
              编辑
            </Button>
          )}
          {checkPermissions(GOODS_RECOMMEND_KEY.UPDATE) && (
            <Button
              type="link"
              disabled={isTaskActive(record.status)}
              onClick={() => handleActivateTask(record)}>
              生效任务
            </Button>
          )}
          {checkPermissions(GOODS_RECOMMEND_KEY.DELETE) && (
            <Button
              type="link"
              danger
              onClick={() => handleDeleteTask(record)}>
              删除
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 16 }}>商品推荐</div>
      <Form
        form={form}
        layout="inline"
        style={{ marginBottom: 16, rowGap: 12 }}
        preserve={false}>
        <Form.Item
          label="任务名称"
          name="taskName">
          <Input
            placeholder="请输入"
            allowClear
            style={{ width: 240 }}
          />
        </Form.Item>
        <Form.Item
          label="任务开始时间"
          name="startTime">
          <RangePicker
            showTime={{
              defaultValue: [dayjs('00:00:00', 'HH:mm:ss'), dayjs('23:59:59', 'HH:mm:ss')],
            }}
            style={{ width: 400 }}
          />
        </Form.Item>
        <Form.Item
          label="更新时间"
          name="updateTime">
          <RangePicker
            showTime={{
              defaultValue: [dayjs('00:00:00', 'HH:mm:ss'), dayjs('23:59:59', 'HH:mm:ss')],
            }}
            style={{ width: 400 }}
          />
        </Form.Item>
        <Form.Item
          label="任务状态"
          name="status">
          <Select
            placeholder="全部"
            style={{ width: 180 }}
            options={TASK_STATUS_OPTIONS}
          />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={() => fetchTaskList({ pageNum: 1 })}>
              查询
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                form.resetFields();
                fetchTaskList({ pageNum: 1 });
              }}>
              重置
            </Button>
          </Space>
        </Form.Item>
      </Form>

      <div style={{ marginBottom: 16 }}>
        {checkPermissions(GOODS_RECOMMEND_KEY.ADD) && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => openDrawer('create')}>
            新建推荐任务
          </Button>
        )}
      </div>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={dataSource}
        pagination={{
          current: pageNum,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: t => `共 ${t} 条`,
          onChange: (page, size) => fetchTaskList({ pageNum: page, pageSize: size }),
        }}
        scroll={{ x: 1400 }}
      />

      {drawerVisible && activeShopId ? (
        <TaskDrawer
          open={drawerVisible}
          mode={drawerMode}
          taskId={currentTaskId}
          shopId={activeShopId}
          shopType={snap.currentShopType}
          platformType={snap.currentShopPlatformType}
          onClose={() => {
            setDrawerVisible(false);
            setCurrentTaskId(null);
          }}
          onSuccess={() => {
            setDrawerVisible(false);
            setCurrentTaskId(null);
            fetchTaskList({ pageNum: 1 });
          }}
        />
      ) : null}
    </div>
  );
};

export default GoodsRecommendList;
